import React from 'react';
import {
  Dropdown, PrimaryButton, Separator, Stack,
} from '@fluentui/react';
import Store from '../../shared/state/Store';
import UiReducer from '../../shared/state/UiReducer';
import Utils from '../../shared/Utils';
import tokens from '../../shared/StackTokens';
import MultiLineUserText from '../../shared/MultiLineUserText';
import CaseManager from '../../shared/state/CaseManager';
import ModellerReducer from '../../modeller/ModellerReducer';
import ModelUtils from '../../modeller/ModelUtils';
import ModellerDefaults from '../../modeller/ModellerDefaults';
import DataUtils from '../../shared/DataUtils';
import DataReducer from '../../shared/state/DataReducer';
import AppReducer from '../../shared/state/AppReducer';
import AppsApi from '../../shared/util/AppsApi';
import FormReducer from '../../shared/state/FormReducer';
import { dataSourceIconSrc } from '../../shared/util/DataSourceIcon';

/** Compact icon size for the external-source dropdown (title + list). */
const EXTERNAL_SOURCE_DROPDOWN_ICON_PX = 24;

function renderExternalSourceDropdownOption(option, iconSizePx) {
  if (!option) {
    return null;
  }
  const iconSrc = option.data?.iconSrc;
  return (
    <Stack horizontal verticalAlign="center" tokens={tokens.spacing5}>
      {iconSrc ? (
        <img
          alt=""
          src={iconSrc}
          width={iconSizePx}
          height={iconSizePx}
          style={{
            objectFit: 'contain',
            flexShrink: 0,
            display: 'block',
          }}
        />
      ) : null}
      <span>{option.text}</span>
    </Stack>
  );
}

export default () => {
  const [globalState, dispatch] = Store.useStore();
  const browseButtonRef = React.useRef(null);
  const [selectedSourceId, setSelectedSourceId] = React.useState();
  const [loadingExternalData, setLoadingExternalData] = React.useState(false);

  const appData = AppReducer.getAppData(globalState);
  const optionalAppViewSources = (appData?.externalSources?.observationSources || [])
    .filter((source) => source?.optional === true && source?.appViewEnabled === true && source?.id);
  const canLoadExternalCaseData = !!appData?.uuid
    && appData?.externalSources?.enabled === true
    && optionalAppViewSources.length > 0;

  React.useEffect(() => {
    if (!canLoadExternalCaseData) {
      setSelectedSourceId(undefined);
      return;
    }
    const currentExists = optionalAppViewSources.some((source) => source.id === selectedSourceId);
    if (!currentExists) {
      setSelectedSourceId(optionalAppViewSources[0].id);
    }
  }, [canLoadExternalCaseData, optionalAppViewSources.map((source) => source.id).join('|')]);

  const clickSelectFile = () => {
    if (browseButtonRef.current) {
      browseButtonRef.current.click();
    }
  };

  const getSelectedFile = (event) => {
    if (event.target.files?.length <= 0) {
      dispatch(UiReducer.uiSetErrors(['No file selected']));
      return false;
    }
    return event.target.files[0];
  };

  const loadJsonFromFile = async (file) => {
    const { size } = file;

    if (size > 104857600) {
      dispatch(UiReducer.uiSetErrors(['File size too big, max 100 Mb allowed']));
      return false;
    }

    const content = await Utils.readFileContents(file);
    let json;
    try {
      json = JSON.parse(content);
    } catch (error) {
      dispatch(UiReducer.uiSetErrors(['File does not contain valid JSON', error.message]));
      return false;
    }

    return json;
  };

  const resetFileBrowseButtonSelection = () => {
    if (browseButtonRef.current) {
      browseButtonRef.current.value = '';
    }
  };

  const validateJson = (json) => {
    try {
      return CaseManager.validateCaseData(json);
    } catch (error) {
      dispatch(UiReducer.uiSetErrors(error.messages || [error.message]));
      return false;
    }
  };

  const loadFile = async (event) => {
    const file = getSelectedFile(event);
    const json = await loadJsonFromFile(file);
    if (json && validateJson(json)) {
      CaseManager.loadCase(dispatch, json);
      dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.isCaseManagerPanelOpen, false));
    }
    resetFileBrowseButtonSelection();
  };

  const loadCaseFromExternalSource = async () => {
    if (!selectedSourceId || !appData?.uuid) {
      return;
    }
    setLoadingExternalData(true);
    try {
      const response = await AppsApi.getCaseData(appData.uuid, selectedSourceId);
      if (response.code >= 300) {
        dispatch(UiReducer.uiSetErrors(response.messages));
        return;
      }

      if ((response.messages || []).length > 0) {
        dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.GeneralSimpleMessageModal, {
          title: 'Advisory',
          messages: response.messages,
        }));
      }

      const externalData = response.data;
      if (!Array.isArray(externalData) || externalData.length === 0) {
        dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.GeneralSimpleMessageModal, {
          title: 'Advisory',
          messages: ['No external data available', ...(response.messages || [])],
        }));
        return;
      }

      const fetchedObservationMap = {};
      externalData.forEach((obs) => {
        const nodeKey = obs?.key || obs?.node;
        if (!obs?.network || !nodeKey) {
          return;
        }
        const key = Utils.createNetworkNodeKey(obs.network, nodeKey);
        fetchedObservationMap[key] = obs;
      });

      const configuredInputItems = ((globalState.config?.inputConfig || [])).flatMap((group) => group.items || []);
      const currentFormData = { ...FormReducer.getFormData(globalState) };
      const currentSoftEvidence = { ...FormReducer.getSoftEvidence(globalState) };
      const currentNodeVariableOverrides = Utils.clone(FormReducer.getNodeVariableOverrides(globalState));

      configuredInputItems.forEach((item) => {
        const itemKey = Utils.createNetworkNodeKey(item.network, item.node);
        const fetchedObservation = fetchedObservationMap[itemKey];
        if (!fetchedObservation) {
          return;
        }

        if (fetchedObservation.constantName) {
          const canApplyConstant = item.allowVariableOverride === true
            && (item.nodeVariables?.[fetchedObservation.constantName] !== undefined);
          if (!canApplyConstant) {
            return;
          }
          const constantValue = fetchedObservation.entries?.[0]?.value ?? fetchedObservation.value;
          if (constantValue === undefined || constantValue === null || constantValue === '') {
            return;
          }
          currentNodeVariableOverrides[item.network] = currentNodeVariableOverrides[item.network] || {};
          currentNodeVariableOverrides[item.network][item.node] = currentNodeVariableOverrides[item.network][item.node] || {};
          currentNodeVariableOverrides[item.network][item.node][fetchedObservation.constantName] = constantValue;
          return;
        }

        if (item.inputModeObservation === false) {
          return;
        }

        const entries = Array.isArray(fetchedObservation.entries)
          ? fetchedObservation.entries.filter((entry) => entry && entry.value !== undefined && entry.value !== null)
          : [];
        const hasSoftEvidence = entries.length > 1;
        const hasSingleEntry = entries.length === 1;

        if (hasSoftEvidence) {
          if (!item.allowSoftEvidence) {
            return;
          }
          const distribution = entries.reduce((dist, entry) => {
            const weight = Number(entry.weight);
            if (!Number.isFinite(weight)) {
              return dist;
            }
            return {
              ...dist,
              [entry.value]: weight,
            };
          }, {});
          if (Object.keys(distribution).length === 0) {
            return;
          }
          currentSoftEvidence[itemKey] = distribution;
          currentFormData[itemKey] = {
            network: item.network,
            node: item.node,
            selected: ModellerDefaults.SoftEvidenceInputOption.value,
          };
          return;
        }

        if (currentSoftEvidence[itemKey]) {
          delete currentSoftEvidence[itemKey];
        }
        const selected = hasSingleEntry ? entries[0].value : fetchedObservation.value;
        if (selected === undefined || selected === null || selected === '') {
          return;
        }
        currentFormData[itemKey] = {
          network: item.network,
          node: item.node,
          selected,
        };
      });

      dispatch(FormReducer.setFormData(currentFormData));
      dispatch(FormReducer.setSoftEvidence(currentSoftEvidence));
      dispatch(FormReducer.setNodeVariableOverrides(currentNodeVariableOverrides));
    } finally {
      setLoadingExternalData(false);
    }
  };

  return (
    <Stack vertical tokens={tokens.spacing}>
      <MultiLineUserText>You can save and load case data (both inputs and outputs) from file or your account</MultiLineUserText>

      <Stack horizontal tokens={tokens.spacing}>
        <span>
          <PrimaryButton
            text="Load Case"
            onClick={clickSelectFile}
          />
          <input ref={browseButtonRef} type="file" accept="application/json" onChange={loadFile} style={{ display: 'none' }} />
        </span>
        <span>
          <PrimaryButton
            text="Save Case"
            onClick={() => {
              const contents = CaseManager.saveCase(globalState);
              Utils.saveToFile('usecase.json', JSON.stringify(contents));
              dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.isCaseManagerPanelOpen, false));
            }}
          />
        </span>
      </Stack>

      {canLoadExternalCaseData && (
        <Stack vertical tokens={tokens.spacing}>
          <Separator />
          <MultiLineUserText>Load case data from source into the form (overwrites selected answers)</MultiLineUserText>
          <Dropdown
            placeholder="Select source"
            selectedKey={selectedSourceId}
            options={optionalAppViewSources.map((source, index) => {
              const iconSrc = dataSourceIconSrc(source.icon);
              return {
                key: source.id,
                text: source.name || `Source ${index + 1}`,
                data: { iconSrc },
              };
            })}
            onChange={(event, option) => {
              setSelectedSourceId(option?.key);
            }}
            onRenderOption={(option) => renderExternalSourceDropdownOption(option, EXTERNAL_SOURCE_DROPDOWN_ICON_PX)}
            onRenderTitle={(selected) => renderExternalSourceDropdownOption(selected?.[0], EXTERNAL_SOURCE_DROPDOWN_ICON_PX)}
            styles={{ dropdown: { minWidth: 250 } }}
          />
          <span>
            <PrimaryButton
              text="Load data"
              disabled={!selectedSourceId || loadingExternalData}
              onClick={loadCaseFromExternalSource}
            />
          </span>
        </Stack>
      )}

      { globalState.config?.generalConfig?.modelViewEnabled && (
        <>
          <Separator />
          <MultiLineUserText>Copy current user case to model view (does not include adjusted prior probabilities):</MultiLineUserText>
          <Stack horizontal tokens={tokens.spacing}>
            <span>
              <PrimaryButton
                text="Copy Case"
                onClick={() => {
                  const updatedModel = { ...ModellerReducer.getModel(globalState) };
                  const caseData = CaseManager.saveCase(globalState);
                  const responseData = caseData.outputData;
                  updatedModel.dataSets = [...updatedModel.dataSets];

                  // Make sure we add a unique ID
                  let newCaseId;
                  const existingIds = updatedModel.dataSets.map((dataset) => dataset.id);
                  for (let i = 0; i < 1000; i += 1) {
                    if (newCaseId && !existingIds.includes(newCaseId)) {
                      break;
                    }
                    newCaseId = `Case ${updatedModel.dataSets.length + i}`;
                  }

                  // Create and push a new case
                  const newCase = {
                    id: newCaseId,
                    results: responseData.results || [],
                    observations: [],
                  };
                  updatedModel.dataSets.push(newCase);

                  const softEvidenceKeys = [];

                  // Add observations to now existing case
                  (Object.values(caseData.inputData || [])).forEach((obsData) => {
                    if (obsData.selected === ModellerDefaults.SoftEvidenceInputOption.value) {
                      // Soft evidence observation
                      // Record key information so we can add an observation a bit later
                      softEvidenceKeys.push({
                        key: Utils.createNetworkNodeKey(obsData.network, obsData.node),
                        networkId: obsData.network,
                        nodeId: obsData.node,
                      });
                      return;
                    }
                    ModelUtils.setObservation({
                      model: updatedModel,
                      datasetId: newCaseId,
                      networkId: obsData.network,
                      nodeId: obsData.node,
                      value: obsData.selected,
                    });
                  });

                  // Now we can iterate over nodes that we know have a soft evidence observation and create an observation for them
                  const { softEvidence } = caseData;
                  softEvidenceKeys.forEach((keyItem) => {
                    ModelUtils.setObservation({
                      model: updatedModel,
                      datasetId: newCaseId,
                      networkId: keyItem.networkId,
                      nodeId: keyItem.nodeId,
                      distribution: softEvidence[keyItem.key],
                    });
                  });

                  const { nodeVariableOverrides } = caseData;
                  Object.keys(nodeVariableOverrides).forEach((networkId) => {
                    Object.keys(nodeVariableOverrides[networkId] || {}).forEach((nodeId) => {
                      Object.keys(nodeVariableOverrides[networkId][nodeId]).forEach((varName) => {
                        ModelUtils.setObservation({
                          model: updatedModel,
                          datasetId: newCaseId,
                          networkId,
                          nodeId,
                          value: nodeVariableOverrides[networkId][nodeId][varName],
                          variableName: varName,
                        });
                      });
                    });
                  });

                  // Update the Modeller's model and cache the raw associated response
                  dispatch(ModellerReducer.updateModel(updatedModel));
                  dispatch(ModellerReducer.cacheCalculationResponse(newCaseId, responseData));
                  dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.GeneralSimpleMessageModal, {
                    title: 'Case copied',
                    message: `Case copied as "${newCaseId}"`,
                  }));
                }}
              />
            </span>
          </Stack>
        </>
      )}

      <Separator />
      <MultiLineUserText>Export results (summary statistics) for the current user case to CSV:</MultiLineUserText>
      <Stack horizontal tokens={tokens.spacing}>
        <span>
          <PrimaryButton
            // disabled={(DataReducer.getCachedResultsForDataSetRaw(globalState, DataReducer.DefaultCaseNames.USER) || []).length === 0}
            text="Export as CSV"
            onClick={() => {
              const caseData = CaseManager.saveCase(globalState);
              const responseData = caseData.outputData;
              const results = responseData?.results;
              if (!results || results.length === 0) {
                dispatch(UiReducer.uiSetErrors(['No results in the User case']));
                return;
              }
              const dataToExport = DataUtils.compileSummaryStatisticsForExport(DataReducer.DefaultCaseNames.USER, results);
              Utils.saveToFile('results.csv', Utils.toCsv(dataToExport));
            }}
          />
        </span>
      </Stack>
    </Stack>
  );
};
